import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { IoMdMegaphone } from 'react-icons/io';
import { FiMail, FiAlertCircle, FiBarChart2, FiMessageCircle, FiPlus, FiSearch, FiChevronDown, FiMoreHorizontal, FiTrash2, FiRotateCcw, FiEye, FiShare2 } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { AnnouncementRow, CommunicationsAnnouncementsResult } from '../../../model/CommunicationsAnnouncementsResult';
import api, { combinedTicker } from '../../../api/api';
import { Announcement } from '../../../model/Stock';
import { toast } from '../Toast';
import { to } from '../../../util/util';
import { getExternalLink } from '../../stock/AnnouncementTable';
import EditAnnouncement from './EditAnnouncement';
import DeleteAnnouncementModal from './DeleteAnnouncementModal';
import NewSocialPostModal from './NewSocialPostModal';

const categories = ['Regular announcement', 'Quarterly report', 'Half-yearly report', 'Annual report', 'Presentation', 'Notice of meeting'];
const pageSizes = [25, 50, 100];

interface Props {
    onChanged?: () => void; // refetch the sub-tab badge counts
}

const Announcements: React.FC<Props> = ({ onChanged }) => {
    const history = useHistory();
    const location = useLocation();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [result, setResult] = useState<CommunicationsAnnouncementsResult | null>(null);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('All');
    const [sensitive, setSensitive] = useState('Any');
    const [email, setEmail] = useState('Any');
    const [year, setYear] = useState('All');
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(1);
    const [adding, setAdding] = useState(false);
    const [reload, setReload] = useState(0);
    const [menuFor, setMenuFor] = useState<AnnouncementRow | null>(null);
    const [postingFor, setPostingFor] = useState<AnnouncementRow | null>(null);
    const [deleting, setDeleting] = useState<AnnouncementRow | null>(null);

    useEffect(() => {
        if (!menuFor) return;
        const onDocClick = (e: MouseEvent) => { if (!(e.target as Element).closest('.row-menu')) setMenuFor(null); };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [menuFor]);

    // dashboard Quick Action links here with ?add=1; drop it so a refresh doesn't reopen the panel
    useEffect(() => {
        if (!new URLSearchParams(location.search).has('add')) return;
        setAdding(true);
        history.replace(location.pathname);
    }, [location.search]);

    useAsyncEffect(async () => {
        if (!stock?.id) return;
        setResult(null);
        const [err, data] = await to(api().getCommunicationsAnnouncements(stock.id));
        if (err) setError(err.message || 'Failed to load announcements');
        else { setError(''); setResult(data); }
    }, [stock?.id, reload]);

    const rows = result?.rows || [];
    const years = useMemo(() => Array.from(new Set(rows.map(r => moment(r.date).format('YYYY')))).sort().reverse(), [rows]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return rows.filter(r =>
            (!q || r.headline.toLowerCase().includes(q)) &&
            (category === 'All' || r.category === category) &&
            (sensitive === 'Any' || (sensitive === 'Yes') === r.priceSensitive) &&
            (email === 'Any' || r.badge === email) &&
            (year === 'All' || moment(r.date).format('YYYY') === year));
    }, [rows, query, category, sensitive, email, year]);

    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const current = Math.min(page, pages);
    const visible = filtered.slice((current - 1) * pageSize, current * pageSize);
    // deleted rows are listed but not counted, so the footer matches the tab badge
    const liveCount = (list: typeof filtered) => list.filter(r => !r.deletedOn).length;
    const liveBefore = liveCount(filtered.slice(0, (current - 1) * pageSize));
    const liveVisible = liveCount(visible);
    const resetPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

    const pageList = (): (number | '…')[] => {
        if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
        const set = new Set([1, 2, 3, current - 1, current, current + 1, pages].filter(p => p >= 1 && p <= pages));
        const out: (number | '…')[] = [];
        Array.from(set).sort((a, b) => a - b).forEach((p, i, arr) => { if (i && p - arr[i - 1] > 1) out.push('…'); out.push(p); });
        return out;
    };

    const select = (label: string, value: string, options: string[], set: (v: string) => void) =>
        <label className="filter">
            <select aria-label={label} value={value} onChange={e => set(e.target.value)}>{options.map(o => <option key={o} value={o}>{label}: {o}</option>)}</select>
            <FiChevronDown />
        </label>;

    const badge = (r: AnnouncementRow) => r.confirmUrl
        ? <a className={`pill ${r.tone}`} href={r.confirmUrl} target="_blank" rel="noopener noreferrer">{r.badge}</a>
        : <span className={`pill ${r.tone}`}>{r.badge}</span>;

    const undelete = async (r: AnnouncementRow) => {
        const announcement = { headline: r.headline, date: r.date } as unknown as Announcement;
        const result = await api().deleteAnnouncement(combinedTicker(stock.ticker, stock.exchange), announcement, true).catch(e => ({ success: false, errorMessage: e.message }));
        if (!result.success) { toast.error(`Undelete failed: ${result.errorMessage}`); return; }
        toast.success('Announcement restored');
        setReload(x => x + 1);
        onChanged?.();
    };

    const Row = ({ r }: { r: AnnouncementRow }) => {
        const d = moment(r.date);
        return <div className="row">
            <div className="date"><div>{d.format('D MMM YYYY')}</div><div className="sub">{d.format('h:mm A')}</div></div>
            <div className="headline">
                <div className="h">{r.priceSensitive && <span className="dot" />}{r.sendGuid
                    ? <Link className={r.deletedOn ? 'deleted' : ''} to={`/communications/announcement/${r.sendGuid}`}>{r.headline}</Link>
                    : <a className={r.deletedOn ? 'deleted' : ''} href={getExternalLink({ localPath: r.localPath })} target="_blank" rel="noopener noreferrer">{r.headline}</a>}</div>
                <div className="meta">
                    <span className={r.priceSensitive ? 'ps' : ''}>{r.priceSensitive ? 'Price sensitive' : 'Not price sensitive'}</span>
                    <span>· PDF</span>
                    {r.deletedOn && <span className="deleted-on">Deleted on: {moment(r.deletedOn).format('D MMM YYYY h:mm A')}</span>}
                    {r.tags.map(t => <span key={t} className="pill">{t}</span>)}
                </div>
            </div>
            <span><span className="pill category">{r.category}</span></span>
            <span className="num">{r.views.toLocaleString()}</span>
            <span className="num">{r.questions.toLocaleString()}</span>
            <span>{badge(r)}</span>
            <span className="row-menu">
                <button type="button" className="more" aria-label="Announcement actions" onClick={() => setMenuFor(menuFor === r ? null : r)}><FiMoreHorizontal /></button>
                {menuFor === r && <div className="menu">
                    {r.sendGuid && <button type="button" onClick={() => history.push(`/communications/announcement/${r.sendGuid}`)}><FiEye /> Open</button>}
                    {!r.deletedOn && <button type="button" onClick={() => { setMenuFor(null); setPostingFor(r); }}><FiShare2 /> Create a Social Post</button>}
                    {r.deletedOn
                        ? <button type="button" onClick={() => { setMenuFor(null); undelete(r); }}><FiRotateCcw /> Undelete Announcement</button>
                        : <button type="button" className="danger" onClick={() => { setMenuFor(null); setDeleting(r); }}><FiTrash2 /> Delete Announcement</button>}
                </div>}
            </span>
        </div>;
    };

    return <div className="announcements">
        <div className="page-header">
            <div>
                <h1>Announcements</h1>
                <p>Every ASX announcement for {stock?.companyName || stock?.ticker}, and the investor email that goes with it</p>
            </div>
            <button type="button" className="btn-primary" onClick={() => setAdding(true)}><FiPlus /> Add Announcement</button>
        </div>
        {adding && <EditAnnouncement onClose={() => setAdding(false)} onSaved={() => { setAdding(false); setReload(r => r + 1); onChanged?.(); }} />}
        {postingFor && <NewSocialPostModal from={postingFor} onClose={() => setPostingFor(null)} />}
        {deleting && <DeleteAnnouncementModal row={deleting} onClose={() => setDeleting(null)} onDeleted={() => { setDeleting(null); setReload(r => r + 1); onChanged?.(); }} />}

        {error && <p className="error">{error}</p>}
        {!error && !result && <p className="loading">Loading announcements…</p>}

        {result && <>
            <div className="stats">
                <div className="stat">
                    <div className="h"><span className="icon-tile"><IoMdMegaphone /></span> This quarter</div>
                    <div className="v">{result.thisQuarter.toLocaleString()}</div>
                    <div className="d">announcements · {result.thisQuarterPriceSensitive.toLocaleString()} price-sensitive</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiMail /></span> Emailed to investors</div>
                    <div className="v">{result.emailed.toLocaleString()}</div>
                    <div className="d">of {result.total.toLocaleString()} · {result.autoSendLabel}</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiAlertCircle /></span> Waiting for approval
                        {result.waiting > 0 && <span className="pill warn">Action needed</span>}</div>
                    <div className="v">{result.waiting.toLocaleString()}</div>
                    <div className="d">{result.waitingDetail}</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiBarChart2 /></span> Views</div>
                    <div className="v">{result.views.toLocaleString()}</div>
                    <div className="d">across {result.total.toLocaleString()} announcements</div>
                </div>
                <div className="stat">
                    <div className="h"><span className="icon-tile"><FiMessageCircle /></span> Questions</div>
                    <div className="v">{result.questions.toLocaleString()}</div>
                    <div className="d">{(result.questions - result.questionsOpen).toLocaleString()} answered · {result.questionsOpen.toLocaleString()} waiting</div>
                </div>
            </div>

            <div className="card">
                <div className="filters">
                    <label className="search"><FiSearch /><input placeholder="Search headlines…" value={query} onChange={e => resetPage(setQuery)(e.target.value)} /></label>
                    {select('Category', category, ['All', ...categories], resetPage(setCategory))}
                    {select('Price Sensitive', sensitive, ['Any', 'Yes', 'No'], resetPage(setSensitive))}
                    {select('Email', email, ['Any', 'Emailed', 'Confirm to send', 'Not emailed'], resetPage(setEmail))}
                    {select('Year', year, ['All', ...years], resetPage(setYear))}
                    <span className="count">Sorted by date, newest first</span>
                </div>

                <div className="table">
                    <div className="row head">
                        <span>Date</span><span>Headline</span><span>Category</span><span className="num">Views</span><span className="num">Questions</span><span>Investor email</span><span />
                    </div>
                    {visible.map(r => <Row key={r.sendGuid || `${r.date}${r.headline}`} r={r} />)}
                    {visible.length === 0 && <p className="empty">No announcements match</p>}
                </div>

                <div className="footer">
                    <span className="count">Showing {liveVisible ? liveBefore + 1 : 0}–{liveBefore + liveVisible} of {liveCount(filtered).toLocaleString()} announcements</span>
                    <label className="filter rows">
                        <select aria-label="Rows per page" value={pageSize} onChange={e => resetPage(setPageSize)(Number(e.target.value))}>{pageSizes.map(n => <option key={n} value={n}>Rows per page: {n}</option>)}</select>
                        <FiChevronDown />
                    </label>
                    <div className="pagination">
                        <button type="button" disabled={current === 1} onClick={() => setPage(current - 1)}>‹</button>
                        {pageList().map((p, i) => p === '…' ? <span key={`e${i}`}>…</span>
                            : <button type="button" key={p} className={p === current ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>)}
                        <button type="button" disabled={current === pages} onClick={() => setPage(current + 1)}>›</button>
                    </div>
                </div>
            </div>
        </>}
    </div>;
};

export default Announcements;
